import React, { useState, useEffect } from "react";
import { baseurl } from "../../baseurl";
import axios from "axios";
import {
  FiPhone, FiMail, FiMapPin, FiHash, FiCalendar,
  FiCheckCircle, FiClock, FiAlertCircle, FiFileText,
} from "react-icons/fi";

/* ─────────────────────────────────────────────
   INLINE CSS
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

.it3__wrap * { box-sizing: border-box; margin: 0; padding: 0; }

.it3__wrap {
  font-family: 'DM Sans', sans-serif;
  background: #dde8e8;
  min-height: 100vh;
  
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* ── paper — exact A4 ── */
.it3__paper {
  width: 210mm;
  min-height: 297mm;
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(10,85,86,0.18), 0 2px 8px rgba(10,85,86,0.10);
  flex-shrink: 0;
}

/* ══════════════════════════════════════
   HEADER — dark teal band
══════════════════════════════════════ */
.it3__header {
  background: linear-gradient(135deg, #0a3d3e 0%, #0a5556 50%, #0c7172 100%);
  padding: 36px 44px 32px;
  position: relative;
  overflow: hidden;
}

/* decorative circles */
.it3__header::before, .it3__header::after {
  content: '';
  position: absolute;
  border-radius: 50%;
  background: rgba(255,255,255,0.04);
}
.it3__header::before { width: 300px; height: 300px; top: -100px; right: -60px; }
.it3__header::after  { width: 180px; height: 180px; bottom: -90px; left: 60px; }

.it3__header-inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;
}

/* brand */
.it3__brand-row { display: flex; align-items: center; gap: 16px; }
.it3__brand-icon {
  width: 54px; height: 54px; border-radius: 16px; flex-shrink: 0;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.20);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-size: 24px; font-weight: 800; color: #FFD166;
}
.it3__business-name {
  font-family: 'Sora', sans-serif; font-size: 22px; font-weight: 800;
  color: #ffffff; letter-spacing: -0.02em; margin-bottom: 8px;
}
.it3__seller-meta { display: flex; flex-direction: column; gap: 5px; }
.it3__seller-row {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; color: rgba(255,255,255,0.65);
}
.it3__seller-row svg { color: #FFD166; flex-shrink: 0; }
.it3__gstin-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px; border-radius: 8px; margin-top: 2px;
  background: rgba(255,209,102,0.15); border: 1px solid rgba(255,209,102,0.30);
  font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 700;
  color: #FFD166; letter-spacing: 0.04em;
}

/* invoice meta right */
.it3__meta-right { text-align: right; }
.it3__invoice-word {
  font-family: 'Sora', sans-serif; font-size: 36px; font-weight: 800;
  color: rgba(255,255,255,0.18); letter-spacing: 0.16em; text-transform: uppercase;
  margin-bottom: 14px; line-height: 1;
}
.it3__meta-rows { display: flex; flex-direction: column; gap: 8px; }
.it3__meta-row  {
  display: flex; align-items: center; gap: 10px; justify-content: flex-end;
}
.it3__meta-icon {
  width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
  background: rgba(255,255,255,0.10); border: 1px solid rgba(255,255,255,0.14);
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,0.55);
}
.it3__meta-lbl {
  font-size: 10px; font-family: 'Sora', sans-serif; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(255,255,255,0.45); min-width: 70px; text-align: right;
}
.it3__meta-val {
  font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 700;
  color: #ffffff; min-width: 90px; text-align: right;
}

/* status badge */
.it3__status {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 50px;
  font-size: 12px; font-family: 'Sora', sans-serif; font-weight: 700;
}
.it3__status--paid    { background: rgba(16,185,129,0.20); color: #6ee7b7; border: 1px solid rgba(16,185,129,0.35); }
.it3__status--unpaid  { background: rgba(239,68,68,0.20);  color: #fca5a5; border: 1px solid rgba(239,68,68,0.35); }
.it3__status--partial { background: rgba(245,158,11,0.20); color: #fcd34d; border: 1px solid rgba(245,158,11,0.35); }
.it3__status--pending { background: rgba(255,255,255,0.10); color: rgba(255,255,255,0.70); border: 1px solid rgba(255,255,255,0.20); }
.it3__status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

/* ══════════════════════════════════════
   BILL TO + META GRID
══════════════════════════════════════ */
.it3__info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding: 28px 44px;
  background: rgba(10,85,86,0.025);
  border-bottom: 1px solid rgba(10,85,86,0.08);
}

.it3__info-card {
  background: #ffffff;
  border: 1px solid rgba(10,85,86,0.10);
  border-radius: 14px; padding: 18px 20px;
  box-shadow: 0 2px 10px rgba(10,85,86,0.05);
}
.it3__card-lbl {
  font-size: 10px; font-family: 'Sora', sans-serif; font-weight: 700;
  letter-spacing: 0.10em; text-transform: uppercase; color: #9ababa; margin-bottom: 12px;
}
.it3__customer-name {
  font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 800;
  color: #0f2a2a; margin-bottom: 9px;
}
.it3__customer-row {
  display: flex; align-items: flex-start; gap: 7px;
  font-size: 12.5px; color: #5a8080; margin-bottom: 6px; line-height: 1.4;
}
.it3__customer-row:last-child { margin-bottom: 0; }
.it3__customer-row svg { color: #14b8a6; flex-shrink: 0; margin-top: 1px; }
.it3__gstin-sm {
  display: inline-flex; align-items: center; padding: 2px 9px;
  border-radius: 7px; font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 700;
  background: rgba(10,85,86,0.07); color: #0a5556; border: 1px solid rgba(10,85,86,0.16);
}

/* details card */
.it3__detail-row {
  display: flex; align-items: center; gap: 12px; padding: 10px 0;
  border-bottom: 1px solid rgba(10,85,86,0.06);
}
.it3__detail-row:first-of-type { padding-top: 0; }
.it3__detail-row:last-child     { border-bottom: none; padding-bottom: 0; }
.it3__detail-icon {
  width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
  background: rgba(10,85,86,0.07);
  display: flex; align-items: center; justify-content: center; color: #0a5556;
}
.it3__detail-lbl { font-size: 10.5px; font-family: 'Sora', sans-serif; font-weight: 600; color: #9ababa; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 2px; }
.it3__detail-val { font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 800; color: #0f2a2a; }

/* ══════════════════════════════════════
   ITEMS TABLE
══════════════════════════════════════ */
.it3__table-section { padding: 28px 44px; }
.it3__table-lbl {
  font-size: 10px; font-family: 'Sora', sans-serif; font-weight: 700;
  letter-spacing: 0.09em; text-transform: uppercase; color: #9ababa; margin-bottom: 14px;
}
.it3__table {
  width: 100%; border-collapse: collapse;
  border: 1px solid rgba(10,85,86,0.09); border-radius: 14px; overflow: hidden;
}
.it3__table thead tr {
  background: linear-gradient(90deg, #0a5556, #0c7172);
}
.it3__table thead th {
  font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255,255,255,0.80);
  padding: 13px 16px; white-space: nowrap; text-align: left;
}
.it3__table thead th:first-child { padding-left: 22px; }
.it3__table thead th.th-r { text-align: right; padding-right: 22px; }
.it3__table thead th.th-c { text-align: center; }

.it3__table tbody tr { border-bottom: 1px solid rgba(10,85,86,0.06); transition: background 0.12s; }
.it3__table tbody tr:last-child { border-bottom: none; }
.it3__table tbody tr:hover { background: rgba(10,85,86,0.025); }
.it3__table tbody td { padding: 13px 16px; font-size: 13px; color: #0f2a2a; vertical-align: middle; }
.it3__table tbody td:first-child { padding-left: 22px; }
.it3__table tbody td.td-r { text-align: right; padding-right: 22px; font-family: 'Sora', sans-serif; font-weight: 600; color: #5a8080; }
.it3__table tbody td.td-c { text-align: center; font-family: 'Sora', sans-serif; font-weight: 700; }
.it3__table tbody td.td-total { text-align: right; padding-right: 22px; }

.it3__item-name { font-size: 13px; font-weight: 700; color: #0f2a2a; margin-bottom: 4px; }
.it3__hsn-chip {
  display: inline-block; padding: 2px 8px; border-radius: 6px;
  background: rgba(10,85,86,0.07); color: #5a8080;
  font-family: 'Sora', sans-serif; font-size: 10.5px; font-weight: 700;
}
.it3__total-val {
  font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 800; color: #0a5556;
}
.it3__empty-row { text-align: center; padding: 40px !important; color: #9ababa; font-family: 'Sora', sans-serif; font-weight: 700; }

/* ══════════════════════════════════════
   FOOTER: notes + summary
══════════════════════════════════════ */
.it3__footer {
  display: flex; align-items: flex-start; gap: 20px;
  padding: 4px 44px 36px;
  flex-wrap: wrap;
}
.it3__notes {
  flex: 1; min-width: 200px;
  background: rgba(10,85,86,0.03); border: 1px solid rgba(10,85,86,0.09);
  border-radius: 14px; padding: 18px 20px;
}
.it3__notes-lbl {
  font-family: 'Sora', sans-serif; font-size: 10px; font-weight: 700;
  letter-spacing: 0.09em; text-transform: uppercase; color: #9ababa; margin-bottom: 8px;
}
.it3__notes-txt { font-size: 13px; color: #5a8080; line-height: 1.6; }

/* summary card */
.it3__summary {
  width: 290px; flex-shrink: 0;
  background: #ffffff; border: 1px solid rgba(10,85,86,0.10);
  border-radius: 16px; overflow: hidden;
  box-shadow: 0 4px 16px rgba(10,85,86,0.07);
}
.it3__sum-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 11px 18px; border-bottom: 1px solid rgba(10,85,86,0.06);
  font-size: 13px;
}
.it3__sum-row:last-of-type { border-bottom: none; }
.it3__sum-lbl { color: #5a8080; font-weight: 500; }
.it3__sum-val { font-family: 'Sora', sans-serif; font-weight: 700; color: #0f2a2a; }

.it3__grand-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 18px;
  background: linear-gradient(135deg, #0a3d3e, #0a5556);
}
.it3__grand-lbl {
  font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 700;
  color: rgba(255,255,255,0.60); text-transform: uppercase; letter-spacing: 0.08em;
}
.it3__grand-val {
  font-family: 'Sora', sans-serif; font-size: 20px; font-weight: 800; color: #FFD166;
}

/* ── bottom footer strip ── */
.it3__bottom {
  background: #0a5556;
  padding: 14px 44px;
  display: flex; align-items: center; justify-content: center;
  gap: 8px;
}
.it3__bottom-txt {
  font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 600;
  color: rgba(255,255,255,0.38); letter-spacing: 0.06em;
}

/* ── print ── */
@page { size: A4 portrait; margin: 0; }
@media print {
  .it3__wrap { background: none !important; padding: 0 !important; display: block !important; }
  .it3__paper {
    width: 210mm !important; min-height: 297mm !important;
    box-shadow: none !important; margin: 0 !important;
  }
}

@media (max-width: 620px) {
  .it3__header { padding: 24px 20px; }
  .it3__info-grid, .it3__table-section { padding-left: 20px; padding-right: 20px; }
  .it3__footer { padding: 4px 20px 28px; flex-direction: column; }
  .it3__summary { width: 100%; }
  .it3__info-grid { grid-template-columns: 1fr; }
  .it3__invoice-word { font-size: 26px; }
  .it3__bottom { padding: 14px 20px; }
}
`;

/* ─── helpers ─── */
const num = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fmtIN = (v) => num(v).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const init  = (name = "") => name.trim()[0]?.toUpperCase() || "?";

const getStatusInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "it3__status--paid",    dot: "#6ee7b7" };
  if (s === "unpaid")  return { cls: "it3__status--unpaid",  dot: "#fca5a5" };
  if (s === "partial") return { cls: "it3__status--partial", dot: "#fcd34d" };
  return                      { cls: "it3__status--pending", dot: "rgba(255,255,255,0.60)" };
};

/* ═══════════════════════════════════════════════════
   COMPONENT — ALL BACKEND LOGIC COMPLETELY UNCHANGED
═══════════════════════════════════════════════════ */
const InvoiceThree = ({ invoiceData }) => {
  const [sellerInfo, setSellerInfo] = useState(null);
  const token = localStorage.getItem("auth_token");

  console.log("invoiceData", invoiceData);

  /* ── FETCH BUSINESS INFO (UNCHANGED) ── */
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${baseurl}getBusinessInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("response", response);
        if (response.data.success) {
          setSellerInfo(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching business info for seller:", error);
      }
    };
    fetchBusinessInfo();
  }, [token]);

  /* ── DEFAULT DATA (UNCHANGED) ── */
  const defaultData = {
    invoice_number: "INV-000",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "N/A",
    payment_status: "Pending",
    notes: "Thank you for your business.",
    seller: { name: "N/A", gstin: "N/A", phone: "N/A", state: "N/A", email: "", zipcode: "" },
    customer: { name: "Client Name", email: "client@example.com", gstin: "N/A", phone: "N/A", state: "N/A", address: "Client Address Here" },
    items: [],
    gst_breakdown: { CGST: 0, SGST: 0 },
    subtotal: 0,
    taxable_amount: 0,
    grand_total: 0,
  };

  /* ── FINAL RESOLVED DATA (UNCHANGED) ── */
  const resolvedSeller =
    invoiceData?.invoice_data?.seller ||
    (sellerInfo ? {
      name: sellerInfo.business_name,
      gstin: sellerInfo.gst || "N/A",
      phone: sellerInfo.whatsapp_number,
      state: sellerInfo.state,
      city: sellerInfo.city,
      zipcode: sellerInfo.zipcode,
      email: sellerInfo.contact_email || sellerInfo.email,
    } : defaultData.seller);

  const resolved = invoiceData?.invoice_data
    ? {
        invoice_number:  invoiceData.invoice_number || defaultData.invoice_number,
        invoice_date:    invoiceData.invoice_data.invoiceDate || defaultData.invoice_date,
        due_date:        invoiceData.invoice_data.dueDate,
        payment_status:  invoiceData.invoice_data.paymentStatus,
        seller:          resolvedSeller,
        customer:        invoiceData.invoice_data.customer || defaultData.customer,
        items:           invoiceData.invoice_data.items || [],
        gst_breakdown:   invoiceData.invoice_data.gstBreakdown || { CGST: 0, SGST: 0 },
        subtotal:        num(invoiceData.invoice_data.itemSubtotal),
        taxable_amount:  num(invoiceData.invoice_data.taxableAmount),
        grand_total:     num(invoiceData.grand_total ?? invoiceData.invoice_data.grandTotal),
        notes:           invoiceData.invoice_data.notes || defaultData.notes,
      }
    : { ...defaultData, seller: resolvedSeller };

  const {
    invoice_number, invoice_date, payment_status,
    seller, customer, items, gst_breakdown,
    subtotal, grand_total, notes,
  } = resolved;

  const si = getStatusInfo(payment_status);

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <div className="it3__wrap">
        <div className="it3__paper">

          {/* ── HEADER (dark teal) ── */}
          <header className="it3__header">
            <div className="it3__header-inner">

              {/* Brand / Seller */}
              <div>
                <div className="it3__brand-row">
                  <div className="it3__brand-icon">{init(seller.name)}</div>
                  <div>
                    <div className="it3__business-name">{seller.name}</div>
                    <div className="it3__seller-meta">
                      {(seller.city || seller.state) && (
                        <div className="it3__seller-row">
                          <FiMapPin size={12} />
                          {seller.city && `${seller.city}, `}{seller.state} {seller.zipcode}
                        </div>
                      )}
                      {seller.phone && (
                        <div className="it3__seller-row"><FiPhone size={12} /> {seller.phone}</div>
                      )}
                      {seller.email && (
                        <div className="it3__seller-row"><FiMail size={12} /> {seller.email}</div>
                      )}
                      {seller.gstin !== "N/A" && (
                        <div className="it3__gstin-chip">GSTIN: {seller.gstin}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Meta */}
              <div className="it3__meta-right">
                <div className="it3__invoice-word">INVOICE</div>
                <div className="it3__meta-rows">
                  <div className="it3__meta-row">
                    <div className="it3__meta-lbl">Invoice No</div>
                    <div className="it3__meta-icon"><FiHash size={12} /></div>
                    <div className="it3__meta-val">{invoice_number}</div>
                  </div>
                  <div className="it3__meta-row">
                    <div className="it3__meta-lbl">Issue Date</div>
                    <div className="it3__meta-icon"><FiCalendar size={12} /></div>
                    <div className="it3__meta-val">{invoice_date}</div>
                  </div>
                  <div className="it3__meta-row">
                    <div className="it3__meta-lbl">Status</div>
                    <div style={{ width: 28 }} />
                    <div className="it3__meta-val">
                      <span className={`it3__status ${si.cls}`}>
                        <span className="it3__status-dot" style={{ background: si.dot }} />
                        {payment_status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </header>

          {/* ── BILL TO + META GRID ── */}
          <div className="it3__info-grid">

            {/* Bill To */}
            <div className="it3__info-card">
              <div className="it3__card-lbl">Bill To</div>
              <div className="it3__customer-name">{customer.name}</div>
              {customer.address && (
                <div className="it3__customer-row"><FiMapPin size={12} /> {customer.address}</div>
              )}
              {customer.phone && (
                <div className="it3__customer-row"><FiPhone size={12} /> {customer.phone}</div>
              )}
              {customer.email && (
                <div className="it3__customer-row"><FiMail size={12} /> {customer.email}</div>
              )}
              {customer.gstin !== "N/A" && customer.gstin && (
                <div style={{ marginTop: 6 }}>
                  <span className="it3__gstin-sm">GSTIN: {customer.gstin}</span>
                </div>
              )}
            </div>

            {/* Invoice Detail Rows */}
            <div className="it3__info-card">
              <div className="it3__card-lbl">Invoice Details</div>
              <div className="it3__detail-row">
                <div className="it3__detail-icon"><FiHash size={14} /></div>
                <div>
                  <div className="it3__detail-lbl">Invoice Number</div>
                  <div className="it3__detail-val">{invoice_number}</div>
                </div>
              </div>
              <div className="it3__detail-row">
                <div className="it3__detail-icon"><FiCalendar size={14} /></div>
                <div>
                  <div className="it3__detail-lbl">Issue Date</div>
                  <div className="it3__detail-val">{invoice_date}</div>
                </div>
              </div>
              <div className="it3__detail-row">
                <div className="it3__detail-icon"><FiCalendar size={14} /></div>
                <div>
                  <div className="it3__detail-lbl">Due Date</div>
                  <div className="it3__detail-val">{resolved.due_date || "N/A"}</div>
                </div>
              </div>
            </div>

          </div>

          {/* ── ITEMS TABLE ── */}
          <div className="it3__table-section">
            <div className="it3__table-lbl">Line Items</div>
            <table className="it3__table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="th-c">HSN</th>
                  <th className="th-c">Qty</th>
                  <th className="th-r">Rate</th>
                  <th className="th-r">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, i) => {
                    const lineTotal = num(item.qty) * num(item.rate) * (1 + num(item.tax) / 100);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="it3__item-name">{item.description}</div>
                        </td>
                        <td className="td-c">
                          <span className="it3__hsn-chip">{item.hsn || "—"}</span>
                        </td>
                        <td className="td-c">{item.qty}</td>
                        <td className="td-r">₹{fmtIN(item.rate)}</td>
                        <td className="td-total">
                          <span className="it3__total-val">₹{fmtIN(lineTotal)}</span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="it3__empty-row">No items found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── FOOTER: notes + summary ── */}
          <div className="it3__footer">

            <div className="it3__notes">
              <div className="it3__notes-lbl">Notes &amp; Remarks</div>
              <div className="it3__notes-txt">{notes || "Thank you for your business."}</div>
            </div>

            <div className="it3__summary">
              <div className="it3__sum-row">
                <span className="it3__sum-lbl">Subtotal</span>
                <span className="it3__sum-val">₹{fmtIN(subtotal)}</span>
              </div>
              <div className="it3__sum-row">
                <span className="it3__sum-lbl">Tax (CGST + SGST)</span>
                <span className="it3__sum-val">
                  ₹{fmtIN(num(gst_breakdown.CGST) + num(gst_breakdown.SGST))}
                </span>
              </div>
              <div className="it3__grand-row">
                <span className="it3__grand-lbl">Amount Due</span>
                <span className="it3__grand-val">₹{fmtIN(grand_total)}</span>
              </div>
            </div>

          </div>

          {/* ── BOTTOM STRIP ── */}
          <div className="it3__bottom">
            <FiFileText size={12} style={{ color: "rgba(255,255,255,0.28)" }} />
            <span className="it3__bottom-txt">This is a computer generated invoice.</span>
          </div>

        </div>
      </div>
    </>
  );
};

export default InvoiceThree;
